import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { CurrentUserOrganization, OrganizationDetails } from "@multi-chat/shared";
import { getCurrentUser } from "../../api/auth-api.js";
import { getOrganization } from "../../api/organizations-api.js";
import { useAuth } from "../auth/auth-context.js";
import { reconcileSelectedOrganizationId } from "./organization-selection.js";
import { subscribeOrganizationAccessLost } from "./organization-suspended-signal.js";
import {
  addToPlatformAdminOrganizationList,
  EMPTY_PLATFORM_ADMIN_ORGANIZATION_LIST,
  readPlatformAdminOrganizationList,
  removeFromPlatformAdminOrganizationList,
  selectionAfterRemoval,
  visiblePlatformAdminOrganizations,
  writePlatformAdminOrganizationList,
  type PlatformAdminOrganizationList,
} from "./platform-admin-organization-list.js";

const STORAGE_KEY = "multi-chat:selected-organization-id";

interface OrganizationContextValue {
  /** Memberships from `/me`; for a Platform Admin, the organizations listed in the switcher. */
  organizations: CurrentUserOrganization[];
  selected: CurrentUserOrganization | null;
  select: (id: number) => void;
  /** Create Organization or accepted invitation: keep this id until memberships include it. */
  selectCreatedOrganization: (id: number) => void;
  /**
   * Organization id held until it resolves: a just-created membership waiting
   * for `/me`, or a Platform Admin selection the server is still authorizing.
   */
  pendingOrganizationId: number | null;
  /** Active PlatformAdmin, as reported by the server on `/me`. */
  platformAdmin: boolean;
  /**
   * Whether a workspace choice was already persisted when the provider mounted.
   * Read-only signal; it does not change the storage semantics above.
   */
  restoredFromStorage: boolean;
  /** Whether a Platform Admin currently lists this organization in the switcher. */
  isInSwitcher: (id: number) => boolean;
  /** Platform Admin: list an organization in the switcher without entering it. */
  addToSwitcher: (id: number) => void;
  /**
   * Platform Admin: stop listing an organization in the switcher. Access is
   * unchanged. Removing the selected organization selects another listed one,
   * or none so the chooser opens.
   */
  removeFromSwitcher: (id: number) => void;
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

function readStoredId(): number | null {
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function storeId(id: number | null): void {
  if (id === null) window.localStorage.removeItem(STORAGE_KEY);
  else window.localStorage.setItem(STORAGE_KEY, String(id));
}

function enteredOrganizationFromDetails(details: OrganizationDetails): CurrentUserOrganization {
  return {
    id: details.id,
    name: details.name,
    role: details.role,
    membershipId: details.membershipId,
    access: details.access,
    memberDisplayName: details.memberDisplayName,
    status: details.status,
  };
}

export function OrganizationProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const { phase, adoptSessionUser } = useAuth();
  const memberships = useMemo(
    () => (phase.kind === "authenticated" ? phase.user.organizations : []),
    [phase],
  );
  const platformAdmin = phase.kind === "authenticated" && phase.user.platformAdmin;

  const userId = phase.kind === "authenticated" ? phase.user.id : null;

  const [selectedId, setSelectedId] = useState<number | null>(() => readStoredId());
  const [pendingCreatedId, setPendingCreatedId] = useState<number | null>(null);
  const [initialStoredId] = useState(() => readStoredId());
  const restoredFromStorage = initialStoredId !== null;

  // Platform Admin switcher list: a device-local preference, never an access control.
  const switcherListOwner = platformAdmin ? userId : null;
  const [switcherListState, setSwitcherListState] = useState(() => ({
    owner: switcherListOwner,
    list:
      switcherListOwner === null
        ? EMPTY_PLATFORM_ADMIN_ORGANIZATION_LIST
        : readPlatformAdminOrganizationList(switcherListOwner, initialStoredId),
  }));
  if (switcherListState.owner !== switcherListOwner) {
    setSwitcherListState({
      owner: switcherListOwner,
      list:
        switcherListOwner === null
          ? EMPTY_PLATFORM_ADMIN_ORGANIZATION_LIST
          : readPlatformAdminOrganizationList(switcherListOwner, initialStoredId),
    });
  }
  const switcherList = switcherListState.list;

  // Listed organizations a Platform Admin is not a member of. The server
  // authorizes each one; the client never trusts a stored id alone.
  const [authorized, setAuthorized] = useState<ReadonlyMap<number, CurrentUserOrganization>>(
    () => new Map(),
  );
  const [deniedIds, setDeniedIds] = useState<ReadonlySet<number>>(() => new Set());
  const [authorizedForUserId, setAuthorizedForUserId] = useState(userId);
  if (authorizedForUserId !== userId) {
    setAuthorizedForUserId(userId);
    setAuthorized(new Map());
    setDeniedIds(new Set());
  }
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const loadingIdsRef = useRef(new Set<number>());

  const membershipIds = useMemo(
    () => new Set(memberships.map((organization) => organization.id)),
    [memberships],
  );
  const idsToAuthorize = platformAdmin
    ? switcherList.added.filter(
        (id) => !membershipIds.has(id) && !authorized.has(id) && !deniedIds.has(id),
      )
    : [];
  const idsToAuthorizeKey = idsToAuthorize.join(",");

  useEffect(() => {
    if (idsToAuthorizeKey === "") return;
    const requestedForUserId = userIdRef.current;
    for (const id of idsToAuthorizeKey.split(",").map(Number)) {
      if (loadingIdsRef.current.has(id)) continue;
      loadingIdsRef.current.add(id);
      getOrganization(id).then(
        (details) => {
          loadingIdsRef.current.delete(id);
          if (userIdRef.current !== requestedForUserId) return;
          setAuthorized((current) => new Map(current).set(id, enteredOrganizationFromDetails(details)));
        },
        () => {
          loadingIdsRef.current.delete(id);
          if (userIdRef.current !== requestedForUserId) return;
          setDeniedIds((current) => new Set(current).add(id));
        },
      );
    }
  }, [idsToAuthorizeKey]);

  const organizations = useMemo(
    () =>
      platformAdmin
        ? visiblePlatformAdminOrganizations({ memberships, list: switcherList, authorized })
        : memberships,
    [authorized, memberships, platformAdmin, switcherList],
  );

  const selectedIsListed =
    selectedId !== null && organizations.some((organization) => organization.id === selectedId);
  const awaitingServerAuthorization =
    phase.kind === "authenticated" &&
    platformAdmin &&
    pendingCreatedId === null &&
    selectedId !== null &&
    !selectedIsListed &&
    !membershipIds.has(selectedId) &&
    switcherList.added.includes(selectedId) &&
    !deniedIds.has(selectedId);

  const updateSwitcherList = useCallback(
    (update: (list: PlatformAdminOrganizationList) => PlatformAdminOrganizationList) => {
      if (switcherListOwner === null) return;
      const next = update(readPlatformAdminOrganizationList(switcherListOwner, initialStoredId));
      writePlatformAdminOrganizationList(switcherListOwner, next);
      setSwitcherListState({ owner: switcherListOwner, list: next });
    },
    [initialStoredId, switcherListOwner],
  );

  // Reconcile only after /me memberships are loaded.
  // An empty list during auth bootstrap must not wipe the persisted organization.
  // A just-created organization stays selected until that membership appears.
  // A Platform Admin selection stays put while the server authorizes it.
  useEffect(() => {
    if (awaitingServerAuthorization) return;
    const membershipsReady = phase.kind === "authenticated";
    const next = reconcileSelectedOrganizationId({
      membershipsReady,
      organizationIds: organizations.map((organization) => organization.id),
      storedId: selectedId,
      pendingCreatedOrganizationId: pendingCreatedId,
    });
    if (next.clearPending) {
      setPendingCreatedId(null);
    }
    if (!membershipsReady && pendingCreatedId === null) return;
    if (next.selectedId !== selectedId) {
      setSelectedId(next.selectedId);
    }
    if (next.persist) {
      storeId(next.selectedId);
    }
  }, [awaitingServerAuthorization, organizations, pendingCreatedId, phase.kind, selectedId]);

  // A 403 ORGANIZATION_ACCESS_DENIED on the selected organization means access
  // was lost while the page was open. Refresh `/me` and re-authorize; the
  // reconcile step then falls back to an organization that is still allowed.
  useEffect(
    () =>
      subscribeOrganizationAccessLost((organizationId) => {
        if (organizationId !== selectedId) return;
        setAuthorized((current) => {
          const next = new Map(current);
          next.delete(organizationId);
          return next;
        });
        setDeniedIds((current) => new Set(current).add(organizationId));
        void getCurrentUser().then(adoptSessionUser, () => undefined);
      }),
    [adoptSessionUser, selectedId],
  );

  const select = useCallback(
    (id: number) => {
      setPendingCreatedId(null);
      setDeniedIds((current) => {
        if (!current.has(id)) return current;
        const next = new Set(current);
        next.delete(id);
        return next;
      });
      updateSwitcherList((list) => addToPlatformAdminOrganizationList(list, id));
      setSelectedId(id);
      storeId(id);
    },
    [updateSwitcherList],
  );

  const selectCreatedOrganization = useCallback(
    (id: number) => {
      updateSwitcherList((list) => addToPlatformAdminOrganizationList(list, id));
      setPendingCreatedId(id);
      setSelectedId(id);
      storeId(id);
    },
    [updateSwitcherList],
  );

  const isInSwitcher = useCallback(
    (id: number) =>
      platformAdmin &&
      (membershipIds.has(id)
        ? !switcherList.hidden.includes(id)
        : switcherList.added.includes(id)),
    [membershipIds, platformAdmin, switcherList],
  );

  const addToSwitcher = useCallback(
    (id: number) => {
      setDeniedIds((current) => {
        if (!current.has(id)) return current;
        const next = new Set(current);
        next.delete(id);
        return next;
      });
      updateSwitcherList((list) => addToPlatformAdminOrganizationList(list, id));
    },
    [updateSwitcherList],
  );

  const removeFromSwitcher = useCallback(
    (id: number) => {
      updateSwitcherList((list) => removeFromPlatformAdminOrganizationList(list, id));
      if (id !== selectedId) return;
      const fallback = selectionAfterRemoval(organizations, id);
      setPendingCreatedId(null);
      setSelectedId(fallback);
      storeId(fallback);
    },
    [organizations, selectedId, updateSwitcherList],
  );

  const selected = useMemo(
    () => organizations.find((org) => org.id === selectedId) ?? null,
    [organizations, selectedId],
  );

  const pendingOrganizationId =
    pendingCreatedId ?? (awaitingServerAuthorization ? selectedId : null);

  const value = useMemo<OrganizationContextValue>(
    () => ({
      organizations,
      selected,
      select,
      selectCreatedOrganization,
      pendingOrganizationId,
      platformAdmin,
      restoredFromStorage,
      isInSwitcher,
      addToSwitcher,
      removeFromSwitcher,
    }),
    [
      organizations,
      pendingOrganizationId,
      platformAdmin,
      selected,
      select,
      selectCreatedOrganization,
      restoredFromStorage,
      isInSwitcher,
      addToSwitcher,
      removeFromSwitcher,
    ],
  );

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}

export function useOrganizations(): OrganizationContextValue {
  const ctx = useContext(OrganizationContext);
  if (!ctx) throw new Error("useOrganizations must be used within an OrganizationProvider");
  return ctx;
}

/** The selected organization is reached through Platform Admin authority, not a membership. */
export function useSelectedPlatformAdminAccess(): boolean {
  const ctx = useContext(OrganizationContext);
  return ctx?.selected?.access === "PLATFORM_ADMIN";
}
